/**
 * Pièces jointes non documentaires – contrôles côté navigateur et dépôt S3 direct.
 *
 * Parcours (API centrale AGORA) :
 *   1. POST /v1/pieces-jointes {ressource, objetId, nom, type, taille}  → Televersement {id, url, champs}
 *   2. POST multipart DIRECTEMENT vers S3 (`url`) : tous les `champs` du formulaire signé, puis le fichier (« file »,
 *      en dernier) – le fichier ne transite pas par l'API ; S3 vérifie la politique (type, taille, préfixe temporaire)
 *   3. POST /v1/pieces-jointes/{id}/confirmation → PieceJointe (l'API contrôle le fichier puis crée la ligne immuable)
 * Les contrôles ci-dessous (liste blanche, taille) évitent un aller-retour inutile ; l'API et S3 les refont.
 */
import {
  TAILLE_MAX_PIECE_JOINTE, TYPES_PIECE_JOINTE, type LienTelechargement, type PieceJointe, type Televersement, type TypePieceJointe,
} from '@agora/shared';
import type { ClientApi } from '../api/client';

/** Valeur de l'attribut `accept` du champ fichier : types mime et extensions de la liste blanche. */
export const ACCEPT_PIECE_JOINTE = Object.entries(TYPES_PIECE_JOINTE)
  .flatMap(([mime, exts]) => [mime, ...exts.map((e) => `.${e}`)])
  .join(',');

/** Extensions acceptées, pour le message de refus (« JPG, JPEG, PNG… »). */
export const FORMATS_PIECE_JOINTE = [...new Set(Object.values(TYPES_PIECE_JOINTE).flat())].map((e) => e.toUpperCase()).join(', ');

export type Controle = { ok: true; type: TypePieceJointe } | { ok: false; raison: 'type' | 'taille' };

/**
 * Type retenu : le type mime annoncé par le navigateur s'il est dans la liste blanche, sinon déduit de l'extension
 * (certains navigateurs n'annoncent pas le HEIC) ; refus si hors liste blanche ou au-delà de la taille maximale.
 */
export function controlerFichier(f: File): Controle {
  const ext = (f.name.split('.').pop() || '').toLowerCase();
  const parMime = (Object.keys(TYPES_PIECE_JOINTE) as TypePieceJointe[]).find((m) => m === f.type);
  const parExt = (Object.entries(TYPES_PIECE_JOINTE) as [TypePieceJointe, readonly string[]][]).find(([, exts]) => exts.includes(ext))?.[0];
  // Type annoncé et extension doivent concorder ; sans type annoncé, l'extension décide.
  const type = parMime ? (parExt && parExt !== parMime ? undefined : parMime) : !f.type ? parExt : undefined;
  if (!type) return { ok: false, raison: 'type' };
  if (f.size <= 0 || f.size > TAILLE_MAX_PIECE_JOINTE) return { ok: false, raison: 'taille' };
  return { ok: true, type };
}

/** Extension affichée sur l'icône de fichier. */
export const extensionDe = (nom: string, type: TypePieceJointe) => {
  const ext = (nom.split('.').pop() || '').toLowerCase();
  return (TYPES_PIECE_JOINTE[type] as readonly string[]).includes(ext) ? ext : TYPES_PIECE_JOINTE[type][0];
};

/**
 * Dépôt direct dans S3 (formulaire POST signé) avec progression. XMLHttpRequest plutôt que fetch : fetch ne donne pas
 * la progression d'un envoi. Mode maquette (`url` « maquette:… ») : fichier gardé en mémoire, progression simulée.
 * `signal` interrompt l'envoi (fichier retiré avant la fin) : la promesse est rejetée avec une AbortError.
 */
export function deposer(t: Televersement, fichier: File, progression: (pct: number) => void, signal?: AbortSignal): Promise<void> {
  const annule = () => new DOMException('annule', 'AbortError');
  if (signal?.aborted) return Promise.reject(annule());
  if (t.url.startsWith('maquette:')) {
    return new Promise((ok, ko) => {
      let pct = 0;
      const id = window.setInterval(() => {
        pct = Math.min(100, pct + 25);
        progression(pct);
        if (pct === 100) {
          window.clearInterval(id);
          signal?.removeEventListener('abort', arreter);
          void import('../api/maquette').then((m) => { m.deposerMaquette(t.id, fichier); ok(); });
        }
      }, 120);
      const arreter = () => { window.clearInterval(id); ko(annule()); };
      signal?.addEventListener('abort', arreter, { once: true });
    });
  }
  return new Promise((ok, ko) => {
    const corps = new FormData();
    for (const [k, v] of Object.entries(t.champs)) corps.append(k, v);
    corps.append('file', fichier);   // le fichier en DERNIER (exigence S3 : les champs après « file » sont ignorés)
    const xhr = new XMLHttpRequest();
    xhr.open('POST', t.url);
    xhr.upload.onprogress = (e) => { if (e.lengthComputable) progression(Math.round((e.loaded / e.total) * 100)); };
    xhr.onload = () => (xhr.status >= 200 && xhr.status < 300 ? ok() : ko(new Error(`S3 ${xhr.status}`)));
    xhr.onerror = () => ko(new Error('reseau'));
    xhr.onabort = () => ko(annule());
    signal?.addEventListener('abort', () => xhr.abort(), { once: true });
    xhr.send(corps);
  });
}

/** Vrai pour le rejet d'un envoi interrompu par son signal (fichier retiré) : rien à signaler à l'utilisateur. */
export const envoiAnnule = (e: unknown) => (e as { name?: string })?.name === 'AbortError';

/**
 * Survol / dépôt de fichiers sur une zone : l'action par défaut du navigateur (ouvrir le fichier et quitter la page)
 * est TOUJOURS annulée, même quand la zone refuse les fichiers (pleine ou désactivée : curseur « interdit »).
 * Renvoie vrai si la zone accepte le dépôt.
 */
export function gererSurvol(e: { preventDefault(): void; dataTransfer: { dropEffect: string } | null }, accepte: boolean): boolean {
  e.preventDefault();
  if (e.dataTransfer) e.dataTransfer.dropEffect = accepte ? 'copy' : 'none';
  return accepte;
}

/**
 * Téléchargement par lien signé (servi en « Content-Disposition: attachment ») : sans quitter la page ni ouvrir de
 * fenêtre (window.open après un appel réseau serait bloqué par certains navigateurs).
 */
export async function telechargerPieceJointe(api: ClientApi, pj: PieceJointe): Promise<void> {
  const lien = await api.get<LienTelechargement>(`/v1/pieces-jointes/${encodeURIComponent(pj.id)}/lien`);
  const a = document.createElement('a');
  a.href = lien.url;
  a.download = pj.nom;
  a.rel = 'noopener';
  document.body.append(a);
  a.click();
  a.remove();
}
